import React from 'react';
import tw from 'twin.macro';

import { AutoSaveState } from 'hooks/useAutoSave';

interface SaveStatusProps {
  state: AutoSaveState;
  keptAt: Date | null;
  /** What was deliberately left out of the last save, if anything. */
  note?: string;
  /**
   * Another campaign was picked and the switch is waiting for this page to be
   * stored first. Without a word here the picker shows the new campaign's
   * name above the old campaign's page with no explanation (review round 7).
   * The wording is Claude's proposal, not yet approved by Steven.
   */
  switching?: boolean;
  /**
   * The screen cannot be stored as it stands, and why. Said instead of
   * "Saved at", which would be untrue for what is on screen. The wording is
   * Claude's proposal, not yet approved by Steven.
   */
  notSavedBecause?: string;
  /**
   * Keep a fixed place (the default). Off where the row has no room for it
   * (the careers page's header).
   */
  steady?: boolean;
}

/** Room for "Not saved, trying again", the longest of the usual sentences. */
const PLACE = tw`inline-flex items-center min-w-[11.5rem] text-left`;

const hourAndMinute = (moment: Date): string =>
  `${String(moment.getHours()).padStart(2, '0')}:${String(
    moment.getMinutes()
  ).padStart(2, '0')}`;

/**
 * The line that replaces the Save button, top right (D20).
 *
 * The exact words are fixed by the description (section 5c) and must not be
 * invented here. It never says "Saved" for something the server did not
 * confirm (done-point 7).
 */
const SaveStatus: React.FC<SaveStatusProps> = ({
  state,
  keptAt,
  note,
  switching = false,
  notSavedBecause,
  steady = true,
}) => {
  const text = (() => {
    if (state === 'failing') return 'Not saved, trying again';
    // D10: the box on the screen says what happened; the corner claims nothing.
    if (state === 'refused') return '';
    if (switching) return 'Saving before switching…';
    if (state === 'saving') return 'Saving…';
    if (state === 'saving-slow') return 'Still saving…';
    if (notSavedBecause) return notSavedBecause;
    if (keptAt) {
      const base = `Saved at ${hourAndMinute(keptAt)}`;
      // 5b item 4: "Saved at 14:02 · salary not saved: numbers only".
      return note ? `${base} · ${note}` : base;
    }
    return '';
  })();

  /*
   * The line keeps its place also while it says nothing, and is as wide as
   * its usual sentences: Undo and Redo sit next to it, and they jumped
   * sideways every time the words changed, so a second click on Undo landed
   * on the wrong spot (Steven's hand test of 5 Oct 2026).
   */
  if (!text) {
    return steady ? <span css={[PLACE]} aria-hidden="true" /> : null;
  }

  return (
    <span
      css={[
        steady && PLACE,
        tw`text-sm font-medium whitespace-nowrap`,
        state === 'failing' ? tw`text-red-600` : tw`text-gray-500`,
      ]}
      data-testid="save-status"
      aria-live="polite"
    >
      {text}
    </span>
  );
};

export default SaveStatus;
